/**
 * Advancing to `DESIGN_WIN` — **one dialog, one required click, under ten
 * seconds.**
 *
 * §6.2 makes that move the commercial close, which means the request needs
 * `close_date`, `outcome`, `actual_annual_revenue` and a reason code, on top of
 * the stage's own `expected_first_revenue_date`, `next_step` and design-
 * engineer contact. Asked as seven mandatory fields, the rational response is
 * to leave the deal at `DESIGN_IN` — under-reporting the headline metric and
 * delaying exactly the win-reason capture §6.2 exists to make timely.
 *
 * So: **prefill, don't ask.** `close_date` defaults to today,
 * `customer_annual_revenue` to the rolled-up customer figure, and `outcome` is
 * fixed to `won` and shown read-only because the dialog was reached from a
 * stage move. The only required interaction is one click among the win reason
 * codes.
 *
 * **The revenue field is on the CUSTOMER basis.** §5.5 [R3] point 1:
 * `actual_annual_revenue` is stored on the NET basis always, and the server
 * multiplies by the factor in force and stamps `net_factor_at_close`. A client
 * that prefilled the net figure would double-convert — reintroducing the
 * mixed-basis defect in the one table that is append-only and therefore
 * uncorrectable.
 */

import { useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { reasonCodesFor, useReferenceData } from '@/lib/reference';
import { formatMoney, today } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface DesignWinClosure {
  close_date: string;
  primary_reason_code: string;
  customer_annual_revenue: string;
  lessons_learned?: string;
}

export function DesignWinDialog({
  opportunity,
  targetStageName,
  onClose,
  onConfirm,
  busy = false,
}: {
  opportunity: Record<string, unknown>;
  targetStageName: string;
  onClose: () => void;
  onConfirm: (closure: DesignWinClosure) => void;
  busy?: boolean;
}): React.ReactElement {
  const reference = useReferenceData();
  const winReasons = reasonCodesFor(reference, 'won');

  const [reason, setReason] = useState<string>('');
  const [closeDate, setCloseDate] = useState<string>(today());
  const [revenue, setRevenue] = useState<string>(
    String(opportunity['annual_revenue_customer'] ?? '0'),
  );
  const [lessons, setLessons] = useState('');
  const [showMore, setShowMore] = useState(false);

  const resale = opportunity['price_basis'] === 'distributor_resale';

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Design win — ${String(opportunity['name'] ?? '')}`}
      description={
        <>
          Moving to {targetStageName} closes the commercial opportunity as won (§6.2). Everything
          below is prefilled — pick the reason and you are done.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                close_date: closeDate,
                primary_reason_code: reason,
                customer_annual_revenue: revenue,
                ...(lessons.trim() === '' ? {} : { lessons_learned: lessons.trim() }),
              })
            }
            disabled={reason === '' || busy}
          >
            {busy ? 'Recording…' : 'Record the win'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <p className="mb-2 text-sm font-medium">
            Why did we win? <span className="text-destructive">*</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {winReasons.map((code) => (
              <button
                key={code.code}
                type="button"
                onClick={() => setReason(code.code)}
                aria-pressed={reason === code.code}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm transition-colors',
                  reason === code.code
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input hover:bg-accent',
                )}
              >
                {code.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Captured now, while the person who ran the design review is still in the room — not 18
            to 36 months later by someone who was not there.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Outcome">
            <div className="flex h-9 items-center">
              <Badge variant="success">Won — fixed by the stage move</Badge>
            </div>
          </Field>
          <Field label="Close date">
            <Input
              type="date"
              value={closeDate}
              onChange={(event) => setCloseDate(event.target.value)}
            />
          </Field>
          <Field
            label={resale ? 'Annual revenue (customer resale)' : 'Annual revenue'}
            hint={
              resale
                ? 'Menlo net is computed from the rep firm factor and stored with it.'
                : 'Direct, so customer and net are the same number.'
            }
          >
            <Input
              inputMode="decimal"
              value={revenue}
              onChange={(event) => setRevenue(event.target.value)}
            />
          </Field>
        </div>

        <p className="text-xs text-muted-foreground">
          Rolled up from the product lines:{' '}
          {formatMoney(opportunity['annual_revenue_customer'] as string)} customer
          {resale ? ` · ${formatMoney(opportunity['annual_revenue_net'] as string)} Menlo net` : ''}
          . Alternate lines are excluded.
        </p>

        {showMore ? (
          <Field
            label="Lessons learned (optional)"
            hint="Redactable by an Admin later — it is the one free-text column on an append-only table."
          >
            <Textarea value={lessons} onChange={(event) => setLessons(event.target.value)} />
          </Field>
        ) : (
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2"
            onClick={() => setShowMore(true)}
          >
            Add lessons learned
          </button>
        )}
      </div>
    </Dialog>
  );
}
